import { LitElement, html, nothing } from 'lit';
import { RefiAnalytics } from '@cof/refi-web-sdk';
import { updateMicroStepEvent, displayProgressBarEvent, updateMacroStepEvent } from '@cof/refi-web-sdk/dist/utils/update-progress-bar';
import { DebugService } from './shared/services/debug.service.js';
import { SessionService } from './core/services/session/session.service.js';
import { AppService } from './app.service.js';
import { stratum } from '../constants/stratum.js';
import { REFI_ESIGN_CONST } from '../constants/app.constants.js';
import './e-title/etitle.js';
import { FeatureToggleService } from './shared/services/feature-toggle.service.js';
import { ExperimentService } from './shared/services/experiment.service.js';
import { EsignService } from './shared/services/e-sign.service.js';
import { CookieService } from './shared/services/cookie.service.js';

class RefiEsignApp extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiEsignApp');
    this.isPerf = false;
    this.isIconic = false;
    this.stateCode = '';
    this.isETitle = false;
    let testingParam = '';
    this.experiments = {};
    if (window?.location) {
      testingParam = new URL(window.location.href)?.searchParams?.get('testing');
      this.experimentParam = new URL(window.location.href)?.searchParams?.get('experiment');
      this.featureToggleParam = new URL(window.location.href)?.searchParams?.get('feature');
      this.isIconic = testingParam === 'lighthouse-iconic-test' && !REFI_ESIGN_CONST.isProdEnv;
      this.isPerf = testingParam === 'lighthouse' && !REFI_ESIGN_CONST.isProdEnv;
    }
    this.session = SessionService.session || {};
    if (this.isPerf) {
      this.session = SessionService.mockSession;
    }
    const appId = this.session?.currentApp?.appId || '';
    stratum.setExternalVar('applicationId', appId);
    this.updateStratumMarketingChannel();
    this.loadingApp = false;
    this.existingExperimentIds = [];
    this.onProcessingPage = false;
    this.baseHref = document.querySelector('base').getAttribute('href');
    this._customerExperiences = AppService.customerExperiences !== undefined ? AppService.customerExperiences : [];
    displayProgressBarEvent(true);
    updateMacroStepEvent('Lock in payment', ['Offers', 'Review', 'Contract']);
    updateMicroStepEvent('Review');
    this.addEventListener(REFI_ESIGN_CONST.ROUTE_CHANGE, () => {
      this.requestUpdate();
    });
    this.analytics = new RefiAnalytics('e-sign', ['sitecatalyst', 'potomac'], appId);
    this.featureToggles = [];
  }

  static get properties() {
    return {
      loadingApp: { type: Boolean },
      onProcessingPage: { type: Boolean },
      status: { type: String },
      featureToggles: { type: Object, attribute: 'feature-toggles' },
      experiments: { type: Object, attribute: 'experiments' },
      stateCode: { type: String, attribute: 'state-code' },
      isETitle: { type: Boolean, attribute: 'etitle' },
      preload: { type: String, attribute: 'pre-load' },
    };
  }

  render() {
    this.debug.log('RENDERED', this.baseHref, window.location.pathname);
    if (this.preload === 'true') {
      return nothing;
    }
    if (![405, 406, 407, 408].includes(this.session?.currentApp?.appStatusId)) {
      return html` <div role="main">
        <lit-route component="refi-contract-error" .resolve="${() => this.checkState('contract').then(() => import('./e-sign/e-sign.js'))}"></lit-route>
      </div>`;
    }
    return html` ${this.loadingApp && !this.isIconic ? html` <refi-loading-spinner message="We’re checking to see if your contract is ready" class="_gb-show"></refi-loading-spinner> ` : html` <div role="main">${this.getContractRouting()}</div> `} `;
  }

  connectedCallback() {
    super.connectedCallback();
    // if testing, use qsp
    if (!REFI_ESIGN_CONST.isProdEnv) {
      if (this.experimentParam) {
        this.experiments = {
          Iconic: {
            allocation: this.experimentParam,
            isEligible: true,
          },
        };
      }
      if (this.featureToggleParam) {
        this.featureToggles = [...this.featureToggles, { featureKey: this.featureToggleParam, isEnabled: true }];
      }
    }
    this.loadingApp = true;
    if (!this.isPerf) {
      this.session = SessionService.getSessionFromStorage() || {};
    }
    const appId = this.session?.currentApp?.appId || '';
    stratum.setExternalVar('applicationId', appId);
    displayProgressBarEvent(true);
    if (SessionService.isMockSession) {
      AppService.updateState({ isETitle: this.isETitle });
      this.loadingApp = false;
    } else {
      if (this.preload !== 'true') {
        if (ExperimentService.isIconicAndToggledOn) {
          this.dispatchEvent(
            new CustomEvent('host-app-event', {
              bubbles: true,
              composed: true,
              detail: {
                actionName: 'initiate-animation-transition',
                data: {
                  skipCurtainOpenAnimation: true,
                  minimumAnimationLengthMs: 5000,
                  animationText: 'Drafting your agreement...',
                },
              },
            }),
          );
        } else {
          this.dispatchEvent(
            new CustomEvent('host-app-event', {
              bubbles: true,
              composed: true,
              detail: {
                actionName: 'close-animation',
              },
            }),
          );
        }
      }
      if (this.preload !== 'true') {
        // send off request to get contract w/ pdfs
        EsignService.fetchContract(false, false);
        // request contract details and render page in the meantime
        EsignService.fetchContract(false, true)
          .then(() => {
            AppService.updateState({ isETitle: this.isETitle });
            this.loadingApp = false;
          })
          .catch(error => {
            this.debug.log(`Error was caught: ${JSON.stringify(error)}`);
            this.loadingApp = false;
          });
      }
    }
  }

  getContractRouting() {
    if (ExperimentService.isCxtFlowAndToggledOn) {
      return html` <lit-route component="refi-expanded-contract" .resolve="${() => this.checkState('contract').then(() => import('./e-sign/e-sign.js'))}"></lit-route> `;
    }
    return html` <lit-route component="refi-contract" .resolve="${() => this.checkState('contract').then(() => import('./e-sign/e-sign.js'))}"></lit-route> `;
  }

  updateStratumMarketingChannel() {
    const parsedCookie = CookieService.getCookie(REFI_ESIGN_CONST.MARKETING_ATTRIBUTE_COOKIE)?.split(/\^|\|/);
    const externalIdFromCookie = parsedCookie?.[1]; // oldest non-bau attribution
    if (parsedCookie?.[0] === 'external_id') {
      // sets var to query param external id if first time with offer code, otherwise cookie
      stratum.setExternalVar('marketingChannel', externalIdFromCookie ?? SessionService.getExternalId());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.preload = false;
  }

  updated(changes) {
    super.updated(changes);
    if (changes.has('isETitle')) {
      AppService.updateState({ isETitle: this.isETitle }, true);
    }
    if (changes.has('stateCode')) {
      AppService.updateState({ stateCode: this.stateCode }, true);
    }
    if (changes.has('featureToggles') && this.featureToggles !== null && this.featureToggles.length !== 0) {
      FeatureToggleService.setFeatureToggles(this.featureToggles);
    }
    if (changes.has('experiments') && this.experiments !== null) {
      // loop through this.experiments and add the object name to experimentIds array if it's eligible
      const experimentIds = [];
      Object.keys(this.experiments).forEach(experiment => {
        if (this.experiments[experiment].isEligible) {
          experimentIds.push(this.experiments[experiment].allocation);
        } else {
          experimentIds.push(`${experiment}-ineligible`);
        }
      });
      ExperimentService.updateExperiments(experimentIds, this.existingExperimentIds);
      this.existingExperimentIds = experimentIds;
      FeatureToggleService.updateIsIconicAndToggledOn();
      FeatureToggleService.updateIsCombinedHPOrDelayedHP();
    }
  }

  async checkState() {
    return Promise.resolve();
  }
}

customElements.define('refi-esign-app', RefiEsignApp);
